import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SignOut, Stethoscope, IdentificationCard } from "@phosphor-icons/react";
import InvisiqueLogo from "@/components/site/Logo";
import NotificationBell from "@/components/doctor/NotificationBell";
import DraftDrawer from "@/components/doctor/DraftDrawer";

/**
 * Portal-only header for the Doctor / Clinic Staff workspace.
 * Mirrors AdminLayout's chrome, no consumer marketing nav, just logo,
 * role badge, identity and logout. Renders its children below.
 */
export default function DoctorShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const isClinicStaff = user?.role === "clinic_staff";
    const badgeLabel = isClinicStaff ? "Clinic Staff" : "Provider";
    const BadgeIcon = isClinicStaff ? IdentificationCard : Stethoscope;

    return (
        <div className="min-h-screen bg-secondary/30" data-testid="doctor-shell">
            <header className="bg-card border-b border-border sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
                    <div className="flex items-center gap-4">
                        <InvisiqueLogo size="sm" />
                        {user && (
                            <div className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] tracking-[0.2em] uppercase font-semibold">
                                <BadgeIcon size={10} weight="fill" /> {badgeLabel}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {user && <DraftDrawer />}
                        {user && <NotificationBell />}
                        {user && (
                            <div className="hidden sm:block text-right">
                                <div className="text-sm font-medium">{user.doctor_name || user.name}</div>
                            </div>
                        )}
                        {user && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { logout(); navigate("/doctor-login"); }}
                                data-testid="doctor-logout-btn"
                            >
                                <SignOut size={14} className="mr-1" /> Logout
                            </Button>
                        )}
                    </div>
                </div>
            </header>
            <main>{children}</main>
        </div>
    );
}
